import { type Money } from "./money.ts";
import { DEFAULT_ROUNDING, divideRounded, type RoundingMode } from "./rounding.ts";

/**
 * Percentages are integer basis points, never floats: 1250 = 12.50%
 * (`03-data-model.md` § Conventions). A rate is never rounded — only the money
 * that comes out of applying one is.
 */
export type BasisPoints = number;

export const BP_SCALE = 10_000n;

export function assertBasisPoints(bp: BasisPoints): void {
  if (!Number.isInteger(bp)) {
    throw new TypeError(`Basis points must be an integer, got ${bp}`);
  }
}

/** `m × bp`, rounded once to the minor unit. */
export function applyRate(m: Money, bp: BasisPoints, mode: RoundingMode = DEFAULT_ROUNDING): Money {
  assertBasisPoints(bp);
  return {
    amount: divideRounded(m.amount * BigInt(bp), BP_SCALE, mode),
    currency: m.currency,
  };
}

/**
 * The tax already contained in a tax-inclusive amount:
 * `base − base ÷ (1 + rate)`, computed as `base × bp ÷ (10000 + bp)` so the
 * division happens once.
 *
 * The result is what an inclusive rule *reports*. It is already inside the
 * price and must never be added to the total — `03-data-model.md` § 5.2 names
 * that as the single most likely bug in the product.
 */
export function inclusivePortion(
  m: Money,
  bp: BasisPoints,
  mode: RoundingMode = DEFAULT_ROUNDING,
): Money {
  assertBasisPoints(bp);
  if (bp === 0) return { amount: 0n, currency: m.currency };
  const denominator = BP_SCALE + BigInt(bp);
  if (denominator === 0n) {
    throw new RangeError(`inclusivePortion: rate of ${bp}bp makes the tax-exclusive base zero`);
  }
  return {
    amount: divideRounded(m.amount * BigInt(bp), denominator, mode),
    currency: m.currency,
  };
}

/** The tax-exclusive base underneath a tax-inclusive amount. */
export function exclusiveBase(
  m: Money,
  bp: BasisPoints,
  mode: RoundingMode = DEFAULT_ROUNDING,
): Money {
  const portion = inclusivePortion(m, bp, mode);
  return { amount: m.amount - portion.amount, currency: m.currency };
}

export function percentToBasisPoints(percent: number): BasisPoints {
  const bp = Math.round(percent * 100);
  return bp;
}

export function basisPointsToPercentString(bp: BasisPoints): string {
  assertBasisPoints(bp);
  const negative = bp < 0;
  const abs = Math.abs(bp);
  const whole = Math.trunc(abs / 100);
  const fraction = (abs % 100).toString().padStart(2, "0");
  return `${negative ? "-" : ""}${whole}.${fraction}`;
}
